import { theme } from "@product/scmp-sdk";
import classnames from "classnames";
import type { FunctionComponent } from "react";
import { memo, useCallback, useEffect } from "react";

import {
  getClassNameFromSize,
  getSizeFromRenderedInfo,
} from "~/components/advertisement/ad-slots/helpers";
import {
  useAdProfile,
  useAdUnitPath,
  useUserRoleTarget,
} from "~/components/advertisement/ad-slots/hooks";
import type { OutstreamAdUnit, Targeting } from "~/components/advertisement/ad-slots/types";
import * as aps from "~/components/advertisement/amazon-publisher-service";
import * as gpt from "~/components/advertisement/google-publisher-tag/hooks";
import * as ix from "~/components/advertisement/index-exchange-wrapper/hooks";
import * as prebid from "~/components/advertisement/prebid";

import { SlotContent } from "./styles";

export type Props = {
  adUnit: OutstreamAdUnit;
  className?: string;
  divId?: string;
  targeting?: Targeting;
  zone?: string | null;
};
const Component: FunctionComponent<Props> = ({ adUnit, className, divId, targeting, zone }) => {
  const adUnitPath = useAdUnitPath(adUnit, zone);
  const userRoleTarget = useUserRoleTarget();
  const { adProfile } = useAdProfile();
  const outstreamSizeMappingBuilder = useCallback(
    (builder: googletag.SizeMappingBuilder) =>
      builder
        .addSize(
          [theme.breakpoints.values.xLargeDesktop, 0],
          [[1, 1], "fluid", [640, 360], [640, 480], [300, 250]],
        )
        .addSize(
          [theme.breakpoints.values.mediumDesktop, 0],
          [[1, 1], "fluid", [480, 360], [480, 270], [300, 250]],
        )
        .addSize([theme.breakpoints.values.desktop, 0], [[1, 1], "fluid", [320, 180], [300, 250]])
        .addSize(
          [theme.breakpoints.values.tablet, 0],
          [[1, 1], "fluid", [480, 360], [480, 270], [300, 250]],
        )
        .addSize([0, 0], [[1, 1], "fluid", [320, 180], [300, 250]])
        .build(),
    [],
  );

  const slot = gpt.useDefineSlot(
    adUnitPath,
    [[1, 1], [640, 480], [480, 360], [640, 360], [320, 180], [300, 250], "fluid"],
    {
      ...targeting,
      ...userRoleTarget,
      adProfile,
      ...ix.useTargeting(adUnit),
    },
    { divId, sizeMappingBuilder: outstreamSizeMappingBuilder },
  );
  const slotInfo = gpt.useRenderSlotInfo(slot);

  useEffect(() => {
    if (!slot) return;

    let isCancel = false;
    const apsResponse = aps.fetchBids([
      {
        mediaType: "multi-format",
        multiFormatProperties: {
          display: { sizes: [[300, 250]] },
          video: { sizes: [[640, 480]] },
        },
        slotID: slot.getSlotElementId(),
        slotName: slot.getAdUnitPath(),
      },
    ]);
    const prebidResponse = prebid.requestBids(slot);
    const run = async () => {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("outstream ad slot request timeout"));
        }, 10_000);
      });

      try {
        await Promise.race([
          Promise.all([apsResponse.result, prebidResponse.result]),
          timeoutPromise,
        ]);
        if (isCancel) return;
        googletag.cmd.push(function () {
          googletag.pubads().refresh([slot]);
        });
      } catch (error) {
        console.error("outstream ad slot failed to fetch", { error });
      }
    };
    void run();

    return () => {
      isCancel = true;
      prebidResponse.cleanup();
    };
  }, [slot]);

  return (
    <SlotContent
      className={classnames(className, getClassNameFromSize(getSizeFromRenderedInfo(slotInfo)))}
      id={slot?.getSlotElementId()}
    />
  );
};

Component.displayName = "Slot";
export const Slot = memo(Component);
